/**
 * Store initialization and domain-service integration.
 *
 * Orchestrates auth, settings, and delegates to domain-specific modules:
 *  - conversationsStoreIntegration.ts — conversation CRUD, pagination, search
 *  - remindersStoreIntegration.ts — reminder CRUD, search
 */

import {
  isAuthenticated as checkAuthFromService,
  getToken,
  setToken,
  removeToken,
  onAuthStateChange,
  onTokenRotated,
  recoverAuth,
  initializeBrowserCloseDetection,
} from '@/services/auth';
import { onNewConversation } from '@/services/chat';
import { onConversationActivated } from '@/services/conversations';
import SettingsService, { addSettingsChangeListener } from '@/services/settings';
import UserService from '@/services/user';
import { logger } from '@/utils/logger';
import { resetUserScopedState } from '@shared/services/userScopedState';

import {
  requestActiveConversationReload,
  resetActiveConversationForSignIn,
} from './activeConversationHydration';
import {
  isManualActivationInProgress,
  resetManualActivationFlag,
} from './conversationsStoreIntegration';
import { useAppStore } from './useAppStore';

/** The `/api/auth/me` user; boot and login hand it on so settings need not refetch it. */
type User = Awaited<ReturnType<typeof UserService.getCurrentUser>>;

// Re-export domain services for backward compatibility
export { conversationsService } from './conversationsStoreIntegration';
export { remindersService } from './remindersStoreIntegration';
export { observersService } from './observersStoreIntegration';

// Guard to prevent multiple store initializations (React StrictMode / navigation re-mounts)
let isStoreInitialized = false;
let isStoreInitializing = false;

/**
 * Attempt to recover authentication from stored credentials.
 * Returns the current token and authenticated state after recovery attempt.
 */
const attemptAuthRecovery = (): { token: string | null; authenticated: boolean } => {
  let token = getToken();
  let authenticated = checkAuthFromService();

  if (!token || !authenticated) {
    logger.info('No authentication found, attempting recovery...');
    const recovered = recoverAuth();
    if (recovered) {
      token = getToken();
      authenticated = checkAuthFromService();
    }
  }

  logger.debug('Store initialization:', {
    has_token: !!token,
    is_authenticated: authenticated,
    recovery_attempted: !getToken() && !checkAuthFromService(),
    timestamp: new Date().toISOString(),
  });

  return { token, authenticated };
};

/**
 * Handle errors from user data fetch during auth initialization.
 * Clears auth on 401, falls back to token-only auth otherwise.
 */
const handleAuthError = (
  store: ReturnType<typeof useAppStore.getState>,
  error: unknown,
  token: string
) => {
  logger.error('Error fetching user data:', error);
  if (error instanceof Error && error.message.includes('401')) {
    logger.warn('Token appears to be invalid, clearing authentication');
    removeToken();
    store.setAuthenticated(false);
  } else {
    store.setAuth(null, token);
  }
};

/**
 * Fetch the current user and set auth state on the store.
 * Handles 401 errors by clearing auth, other errors by falling back to token-only auth.
 * Returns the fetched user so settings can reuse it instead of fetching it again.
 */
const initializeAuthState = async (
  store: ReturnType<typeof useAppStore.getState>,
  token: string | null,
  authenticated: boolean
): Promise<User | undefined> => {
  let user: User | undefined;
  if (token && authenticated) {
    try {
      user = await UserService.getCurrentUser();
      store.setAuth(user, token);
    } catch (error) {
      handleAuthError(store, error, token);
    }

    if (getToken()) {
      store.setAuthenticated(true);
    }
  } else {
    store.setAuthenticated(false);
  }
  return user;
};

/**
 * Complete post-auth setup: load settings and browser close detection.
 *
 * The active conversation is NOT loaded here: it and its full event history are
 * the heaviest payload in the app and only the chat page shows them, so the chat
 * surface loads them when it mounts (see activeConversationHydration).
 */
const completeAuthenticatedSetup = async (authenticated: boolean, user: User | undefined) => {
  const currentAuthState = useAppStore.getState().isAuthenticated;
  logger.debug('Settings initialization check:', {
    original_authenticated: authenticated,
    current_auth_state: currentAuthState,
    will_load_settings: currentAuthState,
  });

  if (currentAuthState) {
    await initializeSettings(user);
    initializeBrowserCloseDetection();
  }
};

/**
 * Initializes the store with data from existing services.
 * This should be called once when the app starts.
 */
export const initializeStore = async () => {
  // Prevent duplicate initialization
  if (isStoreInitialized || isStoreInitializing) {
    logger.debug('Store already initialized or initializing, skipping');
    return;
  }
  isStoreInitializing = true;

  const store = useAppStore.getState();

  try {
    const { token, authenticated } = attemptAuthRecovery();
    const user = await initializeAuthState(store, token, authenticated);
    setupServiceListeners();
    await completeAuthenticatedSetup(authenticated, user);
    isStoreInitialized = true;
  } catch (error) {
    logger.error('Error initializing store:', error);
    store.setError('Failed to initialize application state');
  } finally {
    isStoreInitializing = false;
  }
};

/**
 * Initialize settings from the settings service.
 * `user` is the one the caller just fetched, if any; settings reuse it rather
 * than fetching `/api/auth/me` a second time.
 */
const initializeSettings = async (user?: User) => {
  const store = useAppStore.getState();

  try {
    store.setLoading(true);

    const allSettings = await SettingsService.loadAllSettings(user);

    store.setSettings(allSettings);
    store.setIsSettingsLoaded(true);
  } catch (error) {
    logger.error('Error initializing settings:', error);
  } finally {
    store.setLoading(false);
  }
};

/**
 * Reset every user-scoped store and cache (`registerUserScopedReset`) whenever the
 * authenticated user *identity* changes (logout → null, or an in-SPA account
 * switch old-id → new-id), so a same-session switch never renders the previous
 * user's cached data — Home lists, Household counts, and the rest. Driven
 * by the settled `useAppStore.user` (not the auth event) so it reliably catches
 * switches and ignores same-user token refreshes. Vanilla subscribe — the
 * listener already receives (state, prevState), so no subscribeWithSelector.
 * Registered once at module load.
 */
useAppStore.subscribe((state, prev) => {
  if (state.user?.id !== prev.user?.id) {
    resetUserScopedState();
  }
});

/**
 * Keep the store's token current across refreshes. Sockets connect with
 * `authToken`, so a stale one reconnects with a token the server may have
 * expired. Only the token moves: a refresh is not a sign-in, so settings and
 * the user stay as they are. Registered once at module load, so both boot
 * paths (initializeStore and authService.login) get it.
 */
onTokenRotated(token => {
  const { user, setAuth } = useAppStore.getState();
  setAuth(user, token);
});

/**
 * Set up listeners for service events to keep store in sync
 */
const setupServiceListeners = () => {
  const store = useAppStore.getState();

  // Auth state changes
  onAuthStateChange(async (isAuth: boolean) => {
    store.setAuthenticated(isAuth);
    if (!isAuth) {
      store.clearAuth();
    } else {
      logger.info('🔄 Auth state changed to authenticated, loading settings...');
      await initializeSettings();
      initializeBrowserCloseDetection();
    }
  });

  // Settings changes
  addSettingsChangeListener((key: string, value: string) => {
    store.setSetting(key, value);
  });

  // New conversation events
  onNewConversation(() => {
    logger.info('🆕 New conversation event triggered - clearing messages');
    store.clearMessages();
  });

  // Conversation activation events
  onConversationActivated(() => {
    logger.info('🎯 Conversation activated event triggered');

    if (isManualActivationInProgress()) {
      logger.info('⏭️ Skipping duplicate load - conversation was manually activated');
      resetManualActivationFlag();
      return;
    }

    // Reloads now only if a chat surface shows it; otherwise the next mount does.
    requestActiveConversationReload();
  });
};

/**
 * Enhanced auth service that integrates with store
 */
export const authService = {
  login: async (token: string) => {
    logger.info('🔐 authService.login() called');

    try {
      logger.info('💾 Setting token in storage...');
      setToken(token);
      logger.info('✅ Token set successfully');

      const store = useAppStore.getState();

      logger.info('👤 Fetching user data...');
      let user: User | undefined;
      try {
        user = await UserService.getCurrentUser();
        logger.info('✅ User data fetched:', user.email);
        store.setAuth(user, token);
      } catch (error) {
        logger.error('❌ Error fetching user data during login:', error);
        store.setAuth(null, token);
      }

      logger.info('🔓 Setting authenticated state to true');
      store.setAuthenticated(true);

      logger.info('⚙️ Initializing settings...');
      await initializeSettings(user);

      // A new sign-in may be a different user: whatever the store holds is not
      // theirs. The chat surface fetches theirs when it shows it, not here.
      resetActiveConversationForSignIn();

      logger.info('🎉 authService.login() completed successfully');
    } catch (error) {
      logger.error('💥 Error in authService.login():', error);
      throw error;
    }
  },

  logout: () => {
    // Deliberately session-only: this ends the session and clears the in-memory
    // store, and leaves this device's offline copy alone. The user-facing
    // sign-out, which wipes it, is `logout()` in shared/services/auth.ts.
    removeToken();
    const store = useAppStore.getState();
    store.clearAuth();
  },

  isAuthenticated: () => useAppStore.getState().isAuthenticated,

  getToken: () => useAppStore.getState().authToken || getToken(),
};

/**
 * Enhanced settings service that integrates with store
 */
export const settingsService = {
  loadAllSettings: async (): Promise<Record<string, string>> => {
    return SettingsService.loadAllSettings();
  },

  updateAssistantName: async (name: string) => {
    const store = useAppStore.getState();
    try {
      await SettingsService.updateAssistantName(name);
      store.setSetting('assistant_name', name);
      store.setError(null);
    } catch (error) {
      store.setError(error instanceof Error ? error.message : 'Failed to update assistant name');
      throw error;
    }
  },

  updateSetting: async (key: string, value: string, description?: string) => {
    const store = useAppStore.getState();
    try {
      await SettingsService.updateSetting(key, value, description || null);
      store.setSetting(key, value);
      store.setError(null);
    } catch (error) {
      store.setError(error instanceof Error ? error.message : 'Failed to update setting');
      throw error;
    }
  },

  batchUpdateSettings: async (settings: { key: string; value: string; description?: string }[]) => {
    const store = useAppStore.getState();
    try {
      await SettingsService.batchUpdateSettings(settings);

      for (const item of settings) {
        store.setSetting(item.key, item.value);
      }

      store.setError(null);
    } catch (error) {
      store.setError(error instanceof Error ? error.message : 'Failed to batch update settings');
      throw error;
    }
  },
};
